/* Demandes (« Ce que vous demandez », ex-« Avis & réponses » — D-63) — the library of what a campaign asks the customer (D-50, 05/09 night).
   Three kinds: a quiz (questions with a right answer), a question (one question, choices or free
   text), a private feedback box. A review link is not a saved ask: its sites and their shares are
   set inside the visit that asks it (campaign-system review I-5, gate-go word 2), so « Vos avis, en
   rotation » is retired by name. Each ask
   has a category, its configuration, a linked gift — the campaign's roue, one precise gift
   (revealed without a spin), or none: a default the campaign may change per demande (F-2,
   06/09: « une catégorie » is no longer a linked gift — a category never draws) — and says
   where it is used. Mock persistence per world, like the roues; with an API (the ids row, 2b-1) the server's, read into
   lib/mirror, and nothing is written here: a change goes to the mirror, then to the server (2b-2, lib/writes). */
import { currentWorld, type World } from '@/mocks/worlds'
import { apiWrites } from './api'
import { edited, mirror, serverIdOf, setMirror } from './mirror'
import type { Demande, PlatformShare, SocialId } from './campaign' // types only: campaign.ts imports GIFT_WORDS from here, no runtime cycle

export type AskKind = 'quiz' | 'question' | 'retour'
export interface QuizQuestion { id: string; text: string; choices: string[]; correct: number }
export type LinkedGift = { mode: 'roue' } | { mode: 'cadeau'; typeId: string } | { mode: 'aucun' }
export interface Ask {
  id: string
  kind: AskKind
  name: string
  category: string
  /* quiz */
  questions?: QuizQuestion[]
  /* question */
  question?: string
  answerType?: 'choix' | 'libre'
  choices?: string[]
  /** engagement-moments-v1 · a free line beside the chips (`libre` reads back true) — field name from Technical (0027 pack) */
  freeLine?: boolean
  /* retour privé */
  prompt?: string
  /* all */
  endMessage: string
  gift: LinkedGift
  createdAt: string
  /* mock stats — the real ones are events */
  answers?: number
  usedBy?: string[]
}

export const ASK_CATEGORIES = ['Satisfaction', 'Produits', 'Ambiance', 'Service', 'Découverte', 'Général']
export const KIND_LABELS: Record<AskKind, string> = { quiz: 'Quiz', question: 'Question', retour: 'Retour privé' }
export const KIND_SENTENCES: Record<AskKind, string> = {
  quiz: 'Quelques questions avec une bonne réponse — un jeu, et une chose apprise sur le client.',
  question: 'Une question à la fois, sans bonne réponse — vous apprenez quelque chose.',
  retour: 'Un message rien que pour vous, jamais publié.',
}
export const newAskId = () => `a-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`
export const newQuestionId = () => `q-${Math.random().toString(36).slice(2, 7)}`

function seeds(w: World): Ask[] {
  if (w === 'day-one') return []
  const base: Ask[] = [
    { id: 'a-produits', kind: 'quiz', name: 'Nos produits', category: 'Produits', createdAt: '2026-08-20T09:00:00Z', answers: 84, usedBy: ['Rentrée 2026'],
      questions: [
        { id: 'q1', text: 'Quelle gamme utilisez-vous chez vous ?', choices: ['Kérastase', 'L’Oréal Pro', 'Aucune'], correct: 0 },
        { id: 'q2', text: 'À quelle fréquence faites-vous un soin ?', choices: ['Chaque mois', 'Chaque trimestre', 'Jamais'], correct: 0 },
        { id: 'q3', text: 'Notre shampoing signature s’appelle…', choices: ['Éclat', 'Lumière', 'Soie'], correct: 2 },
      ],
      endMessage: 'Merci pour vos réponses !', gift: { mode: 'roue' } },
    { id: 'a-ambiance', kind: 'quiz', name: 'Ambiance du salon', category: 'Ambiance', createdAt: '2026-08-28T09:00:00Z', answers: 0, usedBy: [],
      questions: [
        { id: 'q1', text: 'Quelle musique préférez-vous pendant votre coupe ?', choices: ['Jazz', 'Pop', 'Silence'], correct: 0 },
        { id: 'q2', text: 'Un café ou un thé ?', choices: ['Café', 'Thé'], correct: 0 },
      ],
      endMessage: 'Merci pour vos réponses.', gift: { mode: 'aucun' } },
    { id: 'a-connus', kind: 'question', name: 'Comment nous avez-vous connus ?', category: 'Découverte', createdAt: '2026-08-22T09:00:00Z', answers: 121, usedBy: ['Rentrée 2026'],
      question: 'Comment nous avez-vous connus ?', answerType: 'choix', choices: ['Une amie', 'Google', 'Instagram', 'En passant devant'],
      endMessage: 'Merci — cela nous aide vraiment.', gift: { mode: 'cadeau', typeId: 'g1' } },
    { id: 'a-retour', kind: 'retour', name: 'Retour privé', category: 'Satisfaction', createdAt: '2026-08-20T09:00:00Z', answers: 9, usedBy: [],
      prompt: 'Une chose à nous dire ? Ce message n’est lu que par nous.', endMessage: 'Merci, nous lisons chaque message.', gift: { mode: 'aucun' } },
  ]
  return base
}
const key = () => `bmr.asks.${currentWorld()}`
function stored(): Ask[] | null { try { const raw = localStorage.getItem(key()); if (raw) { const l = JSON.parse(raw); if (Array.isArray(l)) return l } } catch { /* fine */ } return null }
/** The library: with an API the server's (lib/mirror), else the current world's. */
export function getAsks(): Ask[] { return apiWrites ? mirror().asks : worldAsks() }
/** The world's seeds until the merchant saves, then the merchant's list: the demo's, and what the mock server of a build
 *  that points at itself starts from. A « Vos avis, en rotation » saved before I-5 is left out: its sites now live in the
 *  visit, which already holds a copy of them. */
export function worldAsks(): Ask[] { return (stored() ?? seeds(currentWorld())).filter(a => (a.kind as string) !== 'avis') }
export function setAsks(list: Ask[]): void {
  if (apiWrites) { setMirror({ asks: list }); return }
  try { localStorage.setItem(key(), JSON.stringify(list)) } catch { /* fine */ }
}
/** With an API a demande made in this tab is created on the server, a saved one changed there, a removed one archived
    (the messages and answers it collected stay). A screen holding its first id lands on the server's (serverIdOf). */
export function upsertAsk(a0: Ask): void {
  const a = apiWrites ? { ...a0, id: serverIdOf(a0.id) } : a0
  const l = getAsks(); setAsks(l.some(x => x.id === a.id) ? l.map(x => x.id === a.id ? a : x) : [...l, a])
  edited({ kind: 'ask', id: a.id })
}
export function removeAsk(id0: string): void {
  const id = apiWrites ? serverIdOf(id0) : id0
  setAsks(getAsks().filter(x => x.id !== id)); edited({ kind: 'ask-archive', id })
}
export const getAsk = (id: string) => getAsks().find(a => a.id === (apiWrites ? serverIdOf(id) : id))
export function blankAsk(kind: AskKind): Ask {
  const base = { id: newAskId(), kind, name: '', category: kind === 'retour' ? 'Satisfaction' : 'Général', endMessage: kind === 'retour' ? 'Merci, nous lisons chaque message.' : 'Merci pour vos réponses !', gift: { mode: 'roue' } as LinkedGift, createdAt: new Date().toISOString(), answers: 0, usedBy: [] }
  if (kind === 'quiz') return { ...base, questions: [{ id: newQuestionId(), text: '', choices: ['', ''], correct: 0 }] }
  if (kind === 'question') return { ...base, question: '', answerType: 'choix', choices: ['', ''], freeLine: false }
  return { ...base, name: 'Retour privé', prompt: 'Une chose à nous dire ? Ce message n’est lu que par nous.', gift: { mode: 'aucun' } }
}
/* engagement-moments-v1 (07/10) · the API's write limits (Technical's demande-fields pack): a quiz is 1 to 3 questions
   of 2 to 3 answers, a question 2 to 4 chips, each answer ≤ 80 characters. Stored asks above them stay as they are. */
export const QUIZ_MAX_QUESTIONS = 3
export const QUIZ_MAX_CHOICES = 3
export const CHIPS_MAX = 4
export const CHOICE_MAX = 80
/** A thank-you thanks, full stop: the gift was drawn before the demande (the API's GIFT_WORDS, vet B2b). */
export const GIFT_WORDS = /(?<!\p{L})(cadeaux?|gagn\p{L}*|r[ée]compens\p{L}*|lots?|offerts?|offertes?|gifts?|prizes?|rewards?)(?!\p{L})/iu

/** One refusal, with the API's code and field (contract 02 · asks) and the merchant's words. */
export interface AskCheck { code: string; field: string; text: string }
const choicesOk = (cs: string[], min: number, max: number) =>
  cs.length >= min && cs.length <= max && cs.every(c => c.trim() && c.trim().length <= CHOICE_MAX)
/** What the API would refuse in this ask, in its codes — the editor shows `text`, the tests read `code`. */
export function askChecks(a: Ask): AskCheck[] {
  const out: AskCheck[] = []
  if (!a.name.trim()) out.push({ code: 'invalid_field', field: 'name', text: 'Donnez-lui un nom.' })
  if (a.kind === 'quiz') {
    const qs = a.questions ?? []
    if (!qs.length) out.push({ code: 'invalid_field', field: 'questions', text: 'Ajoutez au moins une question.' })
    if (qs.length > QUIZ_MAX_QUESTIONS) out.push({ code: 'too_many_questions', field: 'questions', text: 'Trois questions au plus — un quiz se joue en vingt secondes.' })
    qs.forEach((q, i) => {
      if (!q.text.trim()) out.push({ code: 'invalid_field', field: `questions.${i}.text`, text: `Question ${i + 1} : écrivez la question.` })
      if (!choicesOk(q.choices, 2, QUIZ_MAX_CHOICES)) out.push({ code: 'invalid_choices', field: `questions.${i}.choices`, text: `Question ${i + 1} : deux ou trois réponses, chacune remplie (${CHOICE_MAX} caractères au plus).` })
      if (!(Number.isInteger(q.correct) && q.correct >= 0 && q.correct < q.choices.length)) out.push({ code: 'invalid_field', field: `questions.${i}.correct`, text: `Question ${i + 1} : cochez la bonne réponse.` })
    })
  }
  if (a.kind === 'question') {
    if (!(a.question ?? '').trim()) out.push({ code: 'invalid_field', field: 'question', text: 'Écrivez la question.' })
    if (a.answerType === 'choix' && !choicesOk(a.choices ?? [], 2, CHIPS_MAX)) out.push({ code: 'invalid_choices', field: 'choices', text: `De deux à quatre réponses rapides, chacune remplie (${CHOICE_MAX} caractères au plus) — ou une réponse libre.` })
  }
  if (GIFT_WORDS.test(a.endMessage)) out.push({ code: 'end_message_ties_gift', field: 'endMessage', text: 'Le message de fin remercie, sans parler du cadeau.' })
  return out
}
/** What is wrong with an ask before it can be saved — merchant words. */
export const askIssues = (a: Ask): string[] => askChecks(a).map(c => c.text)
/** The ask as the API stores it: a free answer reads back `freeLine: true`; chips keep their own switch. */
export const normaliseAsk = (a: Ask): Ask => a.kind !== 'question' ? a
  : { ...a, freeLine: a.answerType === 'libre' ? true : !!a.freeLine, choices: a.answerType === 'libre' ? [] : (a.choices ?? []).map(c => c.trim()) }
/* ---------- lib-pack-17 · the Demandes library page (routes/demandes) reads these; the builder's chooser too ---------- */
/** The kinds in the order the chooser offers them. */
export const ASK_KINDS: AskKind[] = ['question', 'quiz', 'retour']
/** One retour privé per establishment (its box is the inbox's): offer it only while none exists. */
export const retourExists = (list: Ask[] = getAsks()) => list.some(a => a.kind === 'retour')
/** The card's one line under the name: what the demande asks, in the merchant's words. */
export function askSummary(a: Ask): string {
  if (a.kind === 'quiz') { const n = a.questions?.length ?? 0; return `${n} question${n > 1 ? 's' : ''}, sans effet sur le cadeau` }
  return (a.kind === 'question' ? a.question : a.prompt) || '—'
}
/** « Dupliquer »: a copy with its own id and name, used nowhere, nothing counted — the merchant edits it next. */
export function duplicateAsk(a: Ask): Ask {
  return { ...a, id: newAskId(), name: `${a.name} (copie)`, usedBy: [], answers: 0, createdAt: new Date().toISOString(),
    questions: a.questions?.map(q => ({ ...q, id: newQuestionId(), choices: [...q.choices] })), choices: a.choices ? [...a.choices] : undefined }
}
export function giftLabel(g: LinkedGift, names: { types: Record<string, string> }): string {
  if (g.mode === 'roue') return 'la roue de la campagne'
  if (g.mode === 'cadeau') return names.types[g.typeId] ?? 'un cadeau'
  return 'sans cadeau lié'
}

/* ---------- migration v3.3 · pack demandes (WF-013/076/077) · the saved « Demande d’avis » and « Réseaux sociaux » ----------
   The reference keeps an invitation to review, and an invitation to follow, as named, reusable library items. The API's
   asks route carries three kinds (quiz · question · retour — contract 02); a review link is no ask of the server's (I-5):
   its sites and shares live in the visit that asks it. So a saved avis / social demande is the merchant's own preset —
   kept in this browser per world in the demo; with an API the workspace's list (GET/PUT /v1/app/library, 04 §4.4,
   lib-pack-19: read into lib/mirror with the rest, replaced whole on each change, the server's answer in its place) —
   and a visit of the builder that picks it takes a COPY of its platforms or networks — the visit stays the one home of the split (I-5), the library
   item the way to name and reuse it. Every such demande is skippable and decides nothing about the gift (D-84):
   « Le cadeau reste disponible, avec ou sans avis. » Multi-network = the app's design (two cards at most, the first
   primary); the reference's % split between networks is not ported ⟦confirmable⟧. */
export type LinkKind = 'avis' | 'social'
export interface LinkAsk {
  id: string
  kind: LinkKind
  name: string
  /** avis · the sites and their shares — one site = 100 %, several must total 100 (the builder's sharesBox rule) */
  platforms: PlatformShare[]
  /** social · the networks shown, two at most, the first = the primary « Suivre » button */
  socials: SocialId[]
  createdAt: string
  usedBy?: string[]
}
export type LibraryKind = AskKind | LinkKind
export type LibraryItem = Ask | LinkAsk
export const isLinkAsk = (x: LibraryItem): x is LinkAsk => x.kind === 'avis' || x.kind === 'social'
export const LINK_KINDS: LinkKind[] = ['avis', 'social']
/** The Type filter in the reference's order: Demande d’avis · Réseaux sociaux · Question · Quiz · Retour privé. */
export const LIBRARY_KINDS: LibraryKind[] = [...LINK_KINDS, ...ASK_KINDS]
export const LINK_KIND_LABELS: Record<LinkKind, string> = { avis: 'Demande d’avis', social: 'Réseaux sociaux' }
export const LIBRARY_KIND_LABELS: Record<LibraryKind, string> = { ...KIND_LABELS, ...LINK_KIND_LABELS }
export const LINK_KIND_SENTENCES: Record<LinkKind, string> = {
  avis: 'Une invitation à laisser un avis sur une plateforme. Le cadeau reste disponible, avec ou sans avis.',
  social: 'Une invitation à vous suivre sur un réseau — passable, jamais une condition du cadeau.',
}
const linkKey = () => `bmr.lienasks.${currentWorld()}`
/** The saved presets as this browser keeps them (the demo; the mock's GET/PUT /v1/app/library reads and writes the same). */
export function storedLinkAsks(): LinkAsk[] {
  try { const raw = localStorage.getItem(linkKey()); if (raw) { const l = JSON.parse(raw); if (Array.isArray(l)) return l } } catch { /* fine */ }
  return []
}
export function storeLinkAsks(list: LinkAsk[]): void { try { localStorage.setItem(linkKey(), JSON.stringify(list)) } catch { /* fine */ } }
/** The library: with an API the server's list (lib/mirror, read at the root), else the current world's. */
export const getLinkAsks = (): LinkAsk[] => (apiWrites ? mirror().library : storedLinkAsks())
/** The whole list at once: with an API the mirror changes now and one PUT sends it (lib/writes); the demo keeps it here. */
export function setLinkAsks(list: LinkAsk[]): void {
  if (apiWrites) { setMirror({ library: list }); edited({ kind: 'library' }); return }
  storeLinkAsks(list)
}
export function upsertLinkAsk(a: LinkAsk): void { const l = getLinkAsks(); setLinkAsks(l.some(x => x.id === a.id) ? l.map(x => x.id === a.id ? a : x) : [...l, a]) }
export function removeLinkAsk(id: string): void { setLinkAsks(getLinkAsks().filter(x => x.id !== id)) }
export const blankLinkAsk = (kind: LinkKind): LinkAsk => ({ id: newAskId(), kind, name: '', platforms: kind === 'avis' ? [{ platform: 'google', share: 100 }] : [], socials: kind === 'social' ? ['instagram'] : [], createdAt: new Date().toISOString(), usedBy: [] })
/** The card's line under the name: « Google · 100 % », « Google 60 % · Facebook 40 % », « Instagram », « Instagram et Facebook ». */
export function linkAskSummary(a: LinkAsk, label: { platform: (p: PlatformShare['platform']) => string; social: (s: SocialId) => string }): string {
  if (a.kind === 'avis') return a.platforms.length === 1 ? `${label.platform(a.platforms[0].platform)} · 100 %` : a.platforms.map(p => `${label.platform(p.platform)} ${p.share} %`).join(' · ') || '—'
  return a.socials.map(label.social).join(' et ') || '—'
}
/** The API's INCENTIVE_WORDS (lib/journey.js, the custom-URL rule): a name that promises a discount, a free thing, a draw. */
export const INCENTIVE_WORDS = /(?<!\p{L})(gratuit\p{L}*|remises?|r[ée]duc\p{L}*|promo\p{L}*|offres?|coupons?|ristournes?|concours|tirages? au sort|bons? d[’' ]achat)(?!\p{L})|\d\s*[%€]|€\s*\d/iu
/** The library presets ruling (19/10, 3): the one text a preset has never ties it to the gift — the API's name_ties_gift. */
export const NAME_TIES_GIFT = 'Le nom de la demande ne parle pas du cadeau.'
export const LINK_NAME_MAX = 60
/** What keeps a saved avis / social demande from being saved — merchant words; the API's checks (04 §4.4), mirrored. */
export function linkAskIssues(a: LinkAsk): string[] {
  const out: string[] = []
  const name = a.name.trim()
  if (!name) out.push('Donnez-lui un nom.')
  else if (name.length > LINK_NAME_MAX) out.push(`${LINK_NAME_MAX} caractères au plus pour le nom.`)
  else if (GIFT_WORDS.test(name) || INCENTIVE_WORDS.test(name)) out.push(NAME_TIES_GIFT)
  if (a.kind === 'avis') {
    if (!a.platforms.length) out.push('Choisissez une plateforme.')
    if (new Set(a.platforms.map(p => p.platform)).size !== a.platforms.length) out.push('Une plateforme n’apparaît qu’une fois.')
    if (a.platforms.length && a.platforms.reduce((s, p) => s + p.share, 0) !== 100) out.push('La répartition doit faire 100 %.')
  } else {
    if (!a.socials.length) out.push('Choisissez un réseau.')
    if (a.socials.length > 2 || new Set(a.socials).size !== a.socials.length) out.push('Deux réseaux au plus, distincts.')
  }
  return out
}
export const duplicateLinkAsk = (a: LinkAsk): LinkAsk => ({ ...a, id: newAskId(), name: `${a.name} (copie)`, usedBy: [], createdAt: new Date().toISOString(), platforms: a.platforms.map(p => ({ ...p })), socials: [...a.socials] })
/** The visit's demande when the builder picks a saved one: a copy of the split (I-5 — the visit holds its own), never an askId. */
export const demandeOfLinkAsk = (a: LinkAsk): Demande => a.kind === 'avis'
  ? { kind: 'avis', platforms: a.platforms.map(p => ({ ...p })), askId: null, gift: { mode: 'roue' } }
  : { kind: 'suivre', platforms: [], askId: null, gift: { mode: 'roue' }, social: a.socials[0], ...(a.socials.length > 1 ? { socials: [...a.socials] } : {}) }
